import * as React from "react"
import { useCmsIntegration } from "@modules/cms/shared/hooks/useCmsIntegration"
import { NarrowSingleColumn } from "@modules/ui/layouts/Containers"
import { Heading, Text, ThemeCss, Spacer } from "gatsby-interface"
import { importSite as importSiteText } from "@modules/locales/default.js"
import Loading from "@modules/ui/components/Loading"
import { IntegrationSuggestions } from "@modules/site/create/shared/components/IntegrationSuggestions"
import { useRepositoryFromSiteQuery } from "@modules/site/create/Import/queries.generated"
import { FormattedMessage } from "@modules/locales"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { HelpLink } from "@modules/ui/components/HelpLink"
import { useIntegrationSuggestions } from "@modules/cms/integration/hooks/useIntegrationSuggestions"

const emphasizedCss: ThemeCss = theme => ({
  color: theme.colors.lilac,
})

const introWrapperCss: ThemeCss = theme => ({
  margin: `${theme.space[7]} 0`,
})

const introParagraphCss: ThemeCss = theme => ({
  margin: `${theme.space[4]} 0`,
})

export type CmsIntegrationProps = {
  siteId: string
  onNext: () => void
}

export default function CmsIntegration({
  siteId,
  onNext,
}: CmsIntegrationProps) {
  const { data } = useRepositoryFromSiteQuery({
    variables: {
      siteId,
    },
  })

  const { cmsIntegrations, loading, error } = useCmsIntegration(siteId)

  const nameWithOwner = data?.repositoryFromSite?.nameWithOwner

  const [
    { suggestedIntegrations },
    suggestionsLoading,
  ] = useIntegrationSuggestions(siteId, cmsIntegrations)

  if (!suggestionsLoading.loading) {
    if (suggestedIntegrations && suggestedIntegrations.length < 1) {
      // Also want to render something here to indicate no integrations detected?
      onNext()
      return (
        <React.Fragment>
          <Heading as="h4">
            No supported integrations found in this site&apos;s{" "}
            <span
              css={{
                fontFamily: `monospace`,
                whiteSpace: `pre`,
              }}
            >
              gatsby-config.js
            </span>
          </Heading>
          <Spacer size={10} />
          <Text>
            <FormattedMessage<never, "link">
              message={
                "You can connect to <link>our officially supported integrations</link> at any time through your site’s settings."
              }
              tags={{
                link: function(content) {
                  return (
                    <a
                      href={`https://support.gatsbyjs.com/hc/en-us/sections/360011112314-Connecting-to-a-Content-Management-System`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {content}
                    </a>
                  )
                },
              }}
            />
          </Text>
        </React.Fragment>
      )
    }
  }

  if (loading || suggestionsLoading.loading) {
    return (
      <Loading
        delay={1000}
        message={importSiteText.messages.loadingIntegrations}
        variant="baby"
      />
    )
  }

  if (error) {
    return (
      <NarrowSingleColumn>
        <ErrorAlert>{error.message}</ErrorAlert>
      </NarrowSingleColumn>
    )
  }

  const docsHelperText = (
    <Text css={introParagraphCss}>
      <FormattedMessage<never, "link">
        message={importSiteText.messages.cmsIntegrationsDocsInfo}
        tags={{
          link: function(content) {
            return (
              <HelpLink href="https://support.gatsbyjs.com/hc/en-us/sections/360011112314-Connecting-to-a-Content-Management-System">
                {content}
              </HelpLink>
            )
          },
        }}
      />
    </Text>
  )
  const experimentHelperText = (
    <span>
      We detected integrations in your site’s <code>gatsby-config.js</code> –
      connect them now to automatically configure environment variables and
      enable Gatsby Preview.
    </span>
  )
  // This should be promoted to the locales module after newSiteSkip experiment cleanup
  const heading = "Set up integrations for <strong>{siteName}</strong>"

  // END EXPERIMENT

  return nameWithOwner ? (
    <React.Fragment>
      <Heading data-cy="add-integration-title">
        <FormattedMessage<"siteName", "strong">
          message={heading}
          values={{
            siteName: nameWithOwner,
          }}
          tags={{
            strong: function(content) {
              return <strong css={emphasizedCss}>{content}</strong>
            },
          }}
        />
      </Heading>
      <div css={introWrapperCss}>
        <Text css={introParagraphCss}>{experimentHelperText}</Text>
      </div>
      <IntegrationSuggestions
        siteId={siteId}
        nameWithOwner={nameWithOwner}
        cmsIntegrations={cmsIntegrations}
      />
      {docsHelperText}
    </React.Fragment>
  ) : null
}
